"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { useUser } from "@/contexts/user-provider";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import type { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import type { PatientSummary } from "@/types/patient";
import { type DeskPhoneCallDirection, useDeskPhoneScreenPopStore } from "@/stores/desk-phone-screen-pop-store";
const SCREEN_POP_ALERT_TYPES = [AlertType.DESK_PHONE_SCREEN_POP, AlertType.DESK_PHONE_SCREEN_POP_DISMISSED];
interface ScreenPopPayload {
  callId: string;
  /**
   * Null for ring-time pops — a ring group dials several handsets at once,
   * so no single phone owns the pop until one answers. Set on the answer
   * and outbound paths. Carried for debugging only; nothing renders it.
   */
  deskPhoneId?: string | null;
  direction: DeskPhoneCallDirection;
  patient: PatientSummary;
  chatId?: string;
}
interface ScreenPopDismissPayload {
  callId: string;
  reason: string;
}

/**
 * Subscribe to desk-phone screen-pop and dismiss events and reflect them in
 * the screen-pop store. Mount this once near the app shell so the
 * pop-up surface stays in sync regardless of which page the user is on.
 */
export function useDeskPhoneScreenPop() {
  const $ = _c(8);
  const add = useDeskPhoneScreenPopStore(_temp);
  const remove = useDeskPhoneScreenPopStore(_temp2);
  const clear = useDeskPhoneScreenPopStore(_temp3);
  const {
    currentUser
  } = useUser();
  const currentUserId = currentUser?.id;
  let t0;
  if ($[0] !== clear) {
    t0 = () => {
      clear();
    };
    $[0] = clear;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== clear || $[3] !== currentUserId) {
    t1 = [currentUserId, clear];
    $[2] = clear;
    $[3] = currentUserId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  if ($[5] !== add || $[6] !== remove) {
    t2 = function handleMessage(message) {
      const {
        type,
        data
      } = message as {
        type: string;
        data: any;
      };
      if (type === AlertType.DESK_PHONE_SCREEN_POP) {
        const pop = data as ScreenPopPayload;
        add({
          callId: pop.callId,
          deskPhoneId: pop.deskPhoneId,
          direction: pop.direction,
          patient: pop.patient,
          chatId: pop.chatId
        });
        return;
      }
      if (type === AlertType.DESK_PHONE_SCREEN_POP_DISMISSED) {
        const dismiss = data as ScreenPopDismissPayload;
        remove(dismiss.callId);
      }
    };
    $[5] = add;
    $[6] = remove;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleMessage = t2;
  useWebSocketCallbackSubscription(SCREEN_POP_ALERT_TYPES, handleMessage, true, "useDeskPhoneScreenPop");
}
function _temp3(s_1) {
  return s_1.clear;
}
function _temp2(s_0) {
  return s_0.remove;
}
function _temp(s) {
  return s.add;
}